import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, Code2, Target, Clock, CheckCircle2, AlertTriangle, Loader2, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProjectDashboard = ({ project }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [project.id]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const completionRate = stats.total_tasks > 0
    ? Math.round((stats.tasks_completed / stats.total_tasks) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Custom Code Objects"
          value={stats.custom_code_objects}
          subtitle={`${stats.high_priority_objects} high priority`}
          icon={Code2}
          color="blue"
          gradient="from-blue-500 to-cyan-600"
        />
        <StatCard
          title="Simplification Items"
          value={stats.simplification_items}
          subtitle="Identified impacts"
          icon={Target}
          color="purple"
          gradient="from-purple-500 to-pink-600"
        />
        <StatCard
          title="Total Effort"
          value={`${stats.total_effort_days}d`}
          subtitle={`${stats.total_effort_hours} hours`}
          icon={Clock}
          color="orange"
          gradient="from-orange-500 to-red-600"
        />
        <StatCard
          title="Task Completion"
          value={`${completionRate}%`}
          subtitle={`${stats.tasks_completed}/${stats.total_tasks} tasks`}
          icon={CheckCircle2}
          color="green"
          gradient="from-green-500 to-emerald-600"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Task Status Breakdown */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <Activity className="w-6 h-6 mr-3 text-blue-600" />
            Task Status Breakdown
          </h3>
          <div className="space-y-4">
            <TaskStatusBar label="To Do" count={stats.tasks_todo} total={stats.total_tasks} color="gray" />
            <TaskStatusBar label="In Progress" count={stats.tasks_in_progress} total={stats.total_tasks} color="blue" />
            <TaskStatusBar label="Completed" count={stats.tasks_completed} total={stats.total_tasks} color="green" />
          </div>
        </div>

        {/* Project Metrics */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <TrendingUp className="w-6 h-6 mr-3 text-purple-600" />
            Project Metrics
          </h3>
          <div className="space-y-4">
            <MetricRow label="Test Cases" value={stats.test_cases} icon={CheckCircle2} />
            <MetricRow label="Impact Analyses" value={stats.impact_analyses} icon={AlertTriangle} />
            <MetricRow label="Project Status" value={project.status} badge icon={Clock} />
            <MetricRow label="Migration Type" value={project.migration_type} icon={Target} />
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, gradient }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
    <div className="flex items-start justify-between mb-4">
      <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-md`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
    </div>
    <p className="text-sm font-semibold text-gray-600 mb-2">{title}</p>
    <p className="text-4xl font-bold text-gray-900 mb-2">{value}</p>
    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
  </div>
);

const TaskStatusBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const colors = {
    gray: 'bg-gray-400',
    blue: 'bg-blue-600',
    green: 'bg-green-600'
  };
  
  return (
    <div>
      <div className="flex justify-between text-sm mb-2">
        <span className="font-semibold text-gray-700">{label}</span>
        <span className="text-gray-600 font-medium">{count} tasks</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div
          className={`${colors[color]} h-3 rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

const MetricRow = ({ label, value, badge, icon: Icon }) => (
  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
    <span className="text-gray-700 font-medium flex items-center">
      {Icon && <Icon className="w-4 h-4 mr-2 text-gray-500" />}
      {label}
    </span>
    {badge ? (
      <span className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-lg text-sm font-semibold capitalize">
        {value}
      </span>
    ) : (
      <span className="font-bold text-gray-900 capitalize">{value}</span>
    )}
  </div>
);
